import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { INITIAL_FILTERS, ITEMS_PER_PAGE, NUMBER_ITEMS, PAGES, PROD_ENDPOINTS, REVIEW_ENDPOINTS } from '../constants/api';
import { SortOptionId } from '../utils/sortOptions';
import { decodeURLToFilters, encodeFiltersToURL } from '../utils/urlManager';

const createApiPayload = (
  selectedFilters: Filters,
  searchTerm: string,
  sort: SortOptionId,
  page: number,
  overrides?: Partial<Record<string, unknown>>
) => ({
  numberItems: ITEMS_PER_PAGE,
  page,
  search: searchTerm,
  sort,
  type: selectedFilters.type,
  countries: selectedFilters.country,
  useCase: selectedFilters.use_case,
  technology: selectedFilters.technology,
  theme: selectedFilters.theme,
  users: selectedFilters.users,
  businessModel: selectedFilters.business_model,
  fvc: selectedFilters.fvc,
  maturity: selectedFilters.maturity,
  organizationType: selectedFilters.organization_type,
  fourBetters: selectedFilters.four_betters,
  sdg: selectedFilters.sdg,
  countryOfOrigin: selectedFilters.origin_country,
  ...overrides,
});

const areFiltersEmpty = (filters: Filters): boolean => {
  return Object.values(filters).every(
    (data) => !Array.isArray(data) || data.length === 0
  );
};

const useAnalyticsFilters = () => {
  const initialUrlState = useMemo(
    () => decodeURLToFilters(window.location.search, INITIAL_FILTERS),
    []
  );

  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortOptionId>('newest');

  const [searchTerm, setSearchTerm] = useState<string>(() => {
    const { search } = decodeURLToFilters(window.location.search, INITIAL_FILTERS);
    return search;
  });

  const [selectedFilters, setSelectedFilters] = useState<Filters>(() => {
    const { filters } = decodeURLToFilters(window.location.search, INITIAL_FILTERS);
    return filters;
  });

  // API response state
  const [countryCount, setCountryCount] = useState(0);
  const [initiativesCount, setInitiativesCount] = useState(0);
  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);
  const [initiatives, setInitiatives] = useState<any[]>([]);
  const [pages, setPages] = useState(0);

  const hasInitialParams = useMemo(() => {
    const hasSearch = searchTerm.trim().length > 0;
    const hasAnyFilter = !areFiltersEmpty(selectedFilters);
    return hasSearch || hasAnyFilter;
  }, [selectedFilters, searchTerm]);

  const filtersEmpty = useMemo(
    () => areFiltersEmpty(selectedFilters),
    [selectedFilters]
  );

  // API calls
  const loadApi = useCallback(
    async (signal: AbortSignal) => {
      setLoading(true);

      try {
        const response = await axios.post(
          PROD_ENDPOINTS.CASCADE_FILTER,
          createApiPayload(selectedFilters, searchTerm, sort, page),
          { signal }
        );

        if (response.status === 200) {
          const {
            data: initiatives,
            filters: apiFilters,
            number_country_of_territory,
            number_initiatives,
            pages,
          } = response.data as AnalyticsResponse;

          setInitiatives(initiatives);
          setPages(pages);
          setCountryCount(number_country_of_territory);
          setInitiativesCount(number_initiatives);
          setFilters(apiFilters);
          setLoading(false);
        }
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          console.error('Error fetching data:', error);
          setLoading(false);
        }
      }
    },
    [selectedFilters, searchTerm, page, sort]
  );

  useEffect(() => {
    setPage(1);
  }, [selectedFilters, searchTerm, sort]);

  useEffect(() => {
    const abortController = new AbortController();
    loadApi(abortController.signal);
    return () => abortController.abort();
  }, [selectedFilters, searchTerm, sort, page, loadApi]);

  // Sync URL with filter state
  useEffect(() => {
    const queryString = encodeFiltersToURL(selectedFilters, searchTerm);
    const newUrl = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname;

    window.history.replaceState({ path: newUrl }, '', newUrl);
  }, [selectedFilters, searchTerm]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const { filters, search } = decodeURLToFilters(
        window.location.search,
        INITIAL_FILTERS
      );
      setSelectedFilters(filters);
      setSearchTerm(search);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const resetFilters = useCallback(() => {
    setSelectedFilters(INITIAL_FILTERS);
    setSearchTerm('');
  }, []);

  return {
    loading,
    initiatives,
    countryCount,
    initiativesCount,
    page,
    setPage,
    pages,
    filters,
    selectedFilters,
    setSelectedFilters,
    resetFilters,
    searchTerm,
    setSearchTerm,
    areFiltersEmpty: filtersEmpty,
    sort,
    setSort,
  };
};

export default useAnalyticsFilters;